'use client';

import { useMemo, useState } from 'react';
import { formatLarge } from '@benzinga/utils';

import { WatchlistFinancials } from '../../data';
import { hasWatchlistTickerAlerts } from '../watchlistAlertStatus';
import { WATCHLIST_QUOTES_TABLE_FRAME_CLASS } from '../portfolioLayoutConstants';
import { formatQuoteChangeCurrency, signedChangeClassName } from '../portfolioFormatters';
import { formatCurrency, formatPercentage, SymbolCell } from './utils';
import {
  PORTFOLIO_TABLE_CELL_CLASS,
  PORTFOLIO_TABLE_NUMERIC_CLASS,
  WATCHLIST_QUOTES_COLUMNS,
  WatchlistQuotesSortGlyphs,
  watchlistQuotesHeaderCellClass,
  watchlistQuotesHeaderLabelClass,
  type WatchlistQuotesSortDir,
  type WatchlistQuotesSortKey,
} from './watchlistQuotesTableChrome';
import { widgetRowHoverClassName } from '../../../../edge/dashboard/_components/widgetChrome';

type SortKey = WatchlistQuotesSortKey;
type SortDir = WatchlistQuotesSortDir;

export type WatchlistQuotesSortState = { key: SortKey | null; dir: SortDir };

export const getNextWatchlistQuotesSortState = (
  current: WatchlistQuotesSortState,
  nextKey: SortKey,
): WatchlistQuotesSortState => {
  if (current.key !== nextKey) {
    return { dir: 'asc', key: nextKey };
  }

  if (current.dir === 'asc') {
    return { dir: 'desc', key: nextKey };
  }

  return { dir: 'desc', key: null };
};

interface WatchlistQuotesTableProps {
  rows: WatchlistFinancials[];
}

const getAlertsEnabled = (row: WatchlistFinancials): boolean => hasWatchlistTickerAlerts(row.watchlistSymbol);

const getSortValue = (row: WatchlistFinancials, key: SortKey): string | number => {
  switch (key) {
    case 'symbol':
      return row.symbol?.toUpperCase() ?? '';
    case 'price':
      return row.quote?.lastTradePrice ?? 0;
    case 'change':
      return row.quote?.change ?? 0;
    case 'changePercent':
      return row.quote?.changePercent ?? 0;
    case 'volume':
      return row.quote?.volume ?? 0;
    case 'marketCap':
      return row.quote?.marketCap ?? 0;
    case 'alerts':
      return getAlertsEnabled(row) ? 1 : 0;
  }
};

const SortableHeader = ({
  align = 'left',
  label,
  onSort,
  sortDir,
  sortKey,
  activeKey,
}: {
  activeKey: SortKey | null;
  align?: 'left' | 'right';
  label: string;
  onSort: (key: SortKey) => void;
  sortDir: SortDir;
  sortKey: SortKey;
}) => {
  const isActive = activeKey === sortKey;

  return (
    <th className={watchlistQuotesHeaderCellClass(align)}>
      <button
        type="button"
        className={`${watchlistQuotesHeaderLabelClass(align, isActive)} appearance-none transition-colors hover:text-slate-700`}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <WatchlistQuotesSortGlyphs isActive={isActive} sortDir={sortDir} />
      </button>
    </th>
  );
};

const ColoredValue = ({ value, format }: { format: 'currency' | 'percentage'; value: number | null | undefined }) => {
  if (value === undefined || value === null) {
    return <span className="text-slate-400">—</span>;
  }

  const formatted = format === 'currency' ? formatQuoteChangeCurrency(value) : formatPercentage(value);
  return <span className={`font-semibold tabular-nums ${signedChangeClassName(value, format)}`}>{formatted}</span>;
};

export const WatchlistQuotesTable = ({ rows }: WatchlistQuotesTableProps) => {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;

    return [...rows].sort((firstRow, secondRow) => {
      const firstValue = getSortValue(firstRow, sortKey);
      const secondValue = getSortValue(secondRow, sortKey);

      if (typeof firstValue === 'string' && typeof secondValue === 'string') {
        const comparison = firstValue.localeCompare(secondValue);
        return sortDir === 'asc' ? comparison : -comparison;
      }

      const comparison = Number(firstValue) - Number(secondValue);
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [rows, sortDir, sortKey]);

  const handleSort = (nextKey: SortKey) => {
    const nextSort = getNextWatchlistQuotesSortState({ dir: sortDir, key: sortKey }, nextKey);
    setSortKey(nextSort.key);
    setSortDir(nextSort.dir);
  };

  return (
    <div className={`hidden sm:block ${WATCHLIST_QUOTES_TABLE_FRAME_CLASS}`}>
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-slate-200 bg-white text-[11px]">
            {WATCHLIST_QUOTES_COLUMNS.map(column => (
              <SortableHeader
                key={column.key}
                align={column.align}
                label={column.label}
                sortKey={column.key}
                activeKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map(row => {
            const price = row.quote?.lastTradePrice;
            const change = row.quote?.change;
            const changePercent = row.quote?.changePercent;
            const volume = row.quote?.volume;
            const marketCap = row.quote?.marketCap;
            const alertsEnabled = getAlertsEnabled(row);

            return (
              <tr key={row.symbol} className={`border-b border-slate-100 last:border-0 ${widgetRowHoverClassName}`}>
                <td className={PORTFOLIO_TABLE_CELL_CLASS}>
                  <SymbolCell record={row} />
                </td>
                <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right font-semibold tabular-nums text-slate-900`}>
                  {price ? formatCurrency(price) : <span className="text-slate-400">—</span>}
                </td>
                <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right`}>
                  <ColoredValue value={price ? change : null} format="currency" />
                </td>
                <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right`}>
                  <ColoredValue value={price ? changePercent : null} format="percentage" />
                </td>
                <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right tabular-nums text-slate-700`}>
                  {volume ? formatLarge(volume) : <span className="text-slate-400">—</span>}
                </td>
                <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right tabular-nums text-slate-700`}>
                  {marketCap ? formatLarge(marketCap) : <span className="text-slate-400">—</span>}
                </td>
                <td className={`${PORTFOLIO_TABLE_CELL_CLASS} ${PORTFOLIO_TABLE_NUMERIC_CLASS} text-right`}>
                  <span className={alertsEnabled ? 'font-semibold text-bzblue-700' : 'text-slate-400'}>
                    {alertsEnabled ? 'On' : 'Off'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
